import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import DestinationCard from "./DestinationCard";
import { Box, Button, Pagination, PaginationItem } from "@mui/material";
import ArrowBack from "@mui/icons-material/ArrowBack";
import Public from "@mui/icons-material/Public";
import { store } from "../../util/store";
import { makeStyles } from "@mui/styles";
import { useSearchParams } from "react-router-dom";

const styles = {
  root: {
    display: "flex",
    flexDirection: "column",
    maxWidth: "100%",
    height: "100%",
    overflow: "auto",
    backgroundColor: "white",
    width: "100%",
    flex: "1 1 0",
  },
  cards: {
    flex: "1",
    overflow: "auto",
    background: "rgb(255, 253, 250)",
  },
  showAllButton: {
    borderRadius: 0,
    justifyContent: "flex-start",
    fontWeight: 700,
    textTransform: "none",
    padding: "10px 20px",
  },
  resultsHeader: {
    padding: "10px 16px 8px",
    borderBottom: "1px solid #e0e0e0",
    textAlign: "center",
  },
  resultsCount: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    fontSize: "14px",
    color: "#333",
    marginBottom: "2px",
  },
  globeIcon: {
    fontSize: "1rem",
    color: "#555",
  },
  resultsHint: {
    fontSize: "12px",
    color: "#888",
  },
  boxShadow: {
    boxShadow: "0px 2px 3px #00000017",
    border: "1px solid #d3d3d36b",
  },
};

const useStyles = makeStyles({
  ul: {
    justifyContent: "space-evenly",
    paddingTop: "10px",
    boxShadow: "0px 5px 7px #000000bd",
    paddingBottom: "10px",
    height: "50px",
  },
  root: {},
});

const PAGE_SIZE = 25;
const LARGE_SCREENSHOT_DOMAINS = [
  "looandlougallery.com",
  "spazionobile.com",
  "chateauderatilly.fr",
  "www.victoragius.com",
  "www.vanderplasgallery.com",
  "annalaudel.gallery",
  "www.andrewkreps.com",
  "www.anningahof.nl",
  "www.galeriavermelho.com.br",
  "www.frac-champagneardenne.org",
];
const LARGE_SCREENSHOT_PRIORITY = LARGE_SCREENSHOT_DOMAINS.reduce((acc, domain, index) => {
  acc[domain] = index;
  return acc;
}, {});
const getScreenshotDomain = (rawUrl) => {
  try {
    const value = String(rawUrl ?? "");
    const url = value.startsWith("http") ? value : "https://" + value;
    return new URL(url).hostname
      .toLowerCase()
      .replace(/[^a-z0-9.-]/gi, "_");
  } catch {
    return null;
  }
};

const DestinationsPanel = (props) => {
  const { markers, map, showAllButton, onShowAll, onCardClick } = props;
  const { searchTerm, typeFilter, countryFilter, types } = useContext(store);
  const [searchParams] = useSearchParams();
  const destinationId = searchParams.get("destinationId");
  const classes = useStyles();
  const [currentPage, setCurrentPage] = useState(1);
  const hasActiveFilters =
    !!destinationId ||
    searchTerm.length > 0 ||
    typeFilter.length > 0 ||
    countryFilter.length > 0;
  const noFilters = !hasActiveFilters;
  const filteredMarkers = useMemo(
    () =>
      markers
        .filter((marker) => {
          if (destinationId) return marker.location.id === destinationId;
          return marker.display === true;
        })
        .sort((a, b) => {
          if (noFilters) {
            const aPriority = LARGE_SCREENSHOT_PRIORITY[
              getScreenshotDomain(a.location?.url)
            ];
            const bPriority = LARGE_SCREENSHOT_PRIORITY[
              getScreenshotDomain(b.location?.url)
            ];

            if (aPriority !== undefined || bPriority !== undefined) {
              if (aPriority === undefined) return 1;
              if (bPriority === undefined) return -1;
              if (aPriority !== bPriority) return aPriority - bPriority;
            }
          }
          return (b.score ?? 0) - (a.score ?? 0);
        }),
    [destinationId, markers, noFilters],
  );
  const topAnchorRef = useRef(null);
  const bottomAnchorRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, showAllButton, typeFilter, countryFilter, destinationId]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filteredMarkers.length / PAGE_SIZE));
    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
  }, [currentPage, filteredMarkers.length]);

  const handlePageChange = (event, value) => {
    topAnchorRef &&
      topAnchorRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "start",
      });
    setCurrentPage(value);
  };

  useEffect(() => {}, []);

  const pageRenderer = (item) => (
    <PaginationItem
      {...item}
      sx={
        item.type === "next" || item.type === "previous"
          ? styles.boxShadow
          : undefined
      }
    />
  );

  const cards = useMemo(
    () =>
      filteredMarkers.slice(
        (currentPage - 1) * PAGE_SIZE,
        currentPage * PAGE_SIZE,
      ),
    [currentPage, filteredMarkers],
  );

  return (
    <div style={styles.root}>
      <div style={styles.resultsHeader}>
        <div style={styles.resultsCount}>
          <Public style={styles.globeIcon} />
          <span>
            <strong>
              {filteredMarkers.length.toLocaleString()} destinations
            </strong>
            {" · "}
            {hasActiveFilters ? "Showing filtered" : "Showing all on map"}
          </span>
        </div>
        <div style={styles.resultsHint}>Tap a result to focus on the map</div>
      </div>
      {showAllButton && (
        <Button
          onClick={onShowAll}
          style={styles.showAllButton}
          startIcon={<ArrowBack />}
        >
          Show all
        </Button>
      )}
      <div style={styles.cards} ref={containerRef}>
        <div id="topAnchor" ref={topAnchorRef} />
        {!cards ||
          (cards.length === 0 && (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              No results found
            </Box>
          ))}
        {cards.map((card, index) => (
            <DestinationCard
              destination={card.location}
              googleMarker={card.googleMarker}
              map={map}
              key={card.location.id ?? card.location.url ?? index}
              isSecond={false}
              sponsoredVariant={undefined}
              types={types}
              onCardClick={onCardClick}
            />
          ))}
        {/* when this span is visible in the viewport, it means the user has
        scrolled to the bottom of the results and we can then show the footer */}
        <div id="bottomAnchor" ref={bottomAnchorRef} />
      </div>
      {filteredMarkers.length > PAGE_SIZE && (
        <Pagination
          count={Math.ceil(filteredMarkers.length / PAGE_SIZE)}
          page={currentPage}
          onChange={handlePageChange}
          variant="text"
          classes={{ ul: classes.ul, root: classes.root }}
          renderItem={pageRenderer}
        />
      )}
    </div>
  );
};

export default DestinationsPanel;
